import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, X, Send, CheckCircle } from 'lucide-react';

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', 'suggestion-type': 'feedback', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;
    setLoading(true);
    try {
      const formData = new URLSearchParams();
      formData.append('form-name', 'suggestions');
      formData.append('name', form.name || 'Anonymous');
      formData.append('suggestion-type', form['suggestion-type']);
      formData.append('message', form.message);

      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setOpen(false); setForm({ name: '', 'suggestion-type': 'feedback', message: '' }); }, 2000);
    } catch {
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setOpen(false); }, 2000);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="feedback-button"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-full brand-button text-white font-semibold text-sm shadow-xl shadow-violet-900/20 hover:-translate-y-0.5 transition-all">
        <Lightbulb className="w-4 h-4" /> Suggest Data or Features
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setOpen(false)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="feedback-modal">
              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-slate-900">Thank you!</p>
                  <p className="text-sm text-slate-500">Your suggestion has been submitted.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg font-semibold text-slate-900">Suggest Data or Features</h2>
                    <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-violet-50 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
                  </div>
                  <form name="suggestions" method="POST" data-netlify="true" onSubmit={handleSubmit} className="space-y-4">
                    <input type="hidden" name="form-name" value="suggestions" />
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Name (optional)</label>
                      <input type="text" name="name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Anonymous"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none text-sm" data-testid="feedback-name" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Type</label>
                      <select name="suggestion-type" value={form['suggestion-type']} onChange={e => setForm({...form, 'suggestion-type': e.target.value})}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none text-sm bg-white" data-testid="feedback-type">
                        <option value="new_dataset">New Dataset</option>
                        <option value="question">Question</option>
                        <option value="feedback">Feedback</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
                      <textarea name="message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows="4" placeholder="What data, explanation, or feature would you like to see next?"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none text-sm resize-none" data-testid="feedback-message" />
                    </div>
                    <button type="submit" disabled={loading || !form.message.trim()} data-testid="feedback-submit"
                      className="w-full py-3 rounded-xl brand-button text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all">
                      <Send className="w-4 h-4" /> {loading ? 'Sending...' : 'Submit'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
